import axios from "axios";
import { _throw } from "../utils/error-service";
import { API } from '../constants/system';

/**
 * Fetches all scraper integrations across merchants (admin overview).
 * @param {string} token - The authorization token.
 * @returns {Promise<object>} The API response.
 */
export const api_get_scrapers = async (token) => {
  try {
    const res = await axios.get(`${API}/cms/scrapers`, {
      headers: {
        'Authorization': token
      }
    });
    return res;
  } catch (err) {
    console.error('Error fetching scrapers:', err);
    return _throw(err);
  }
};

/**
 * Attaches a scraper to a dispensary. Once attached, the scraper becomes the
 * source of truth for the merchant's catalog.
 * @param {string|number} merchantId - The ID of the merchant.
 * @param {object} scraperData - { provider, storeId, menuUrl, name }
 * @param {string} token - The authorization token.
 * @returns {Promise<object>} The API response.
 */
export const api_attach_scraper = async (merchantId, scraperData, token) => {
  if (!merchantId) {
    console.error('api_attach_scraper: merchantId is required');
    return _throw({ response: { data: { message: 'Merchant ID is required' } } });
  }
  try {
    const res = await axios.post(`${API}/cms/scrapers/${merchantId}`, scraperData, {
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    });
    return res;
  } catch (err) {
    console.error(`Error attaching scraper to merchant ${merchantId}:`, err);
    return _throw(err);
  }
};

/**
 * Updates a scraper integration (status, storeId, menuUrl, name, syncProducts).
 * @param {string|number} integrationId - The ID of the scraper integration.
 * @param {object} scraperData - Fields to update.
 * @param {string} token - The authorization token.
 * @returns {Promise<object>} The API response.
 */
export const api_update_scraper = async (integrationId, scraperData, token) => {
  if (!integrationId) {
    console.error('api_update_scraper: integrationId is required');
    return _throw({ response: { data: { message: 'Integration ID is required' } } });
  }
  try {
    const res = await axios.put(`${API}/cms/scrapers/${integrationId}`, scraperData, {
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    });
    return res;
  } catch (err) {
    console.error(`Error updating scraper ${integrationId}:`, err);
    return _throw(err);
  }
};

/**
 * Detaches (deletes) a scraper integration. Products it created stay in place.
 * @param {string|number} integrationId - The ID of the scraper integration.
 * @param {string} token - The authorization token.
 * @returns {Promise<object>} The API response.
 */
export const api_detach_scraper = async (integrationId, token) => {
  if (!integrationId) {
    console.error('api_detach_scraper: integrationId is required');
    return _throw({ response: { data: { message: 'Integration ID is required' } } });
  }
  try {
    const res = await axios.delete(`${API}/cms/scrapers/${integrationId}`, {
      headers: {
        'Authorization': token
      }
    });
    return res;
  } catch (err) {
    console.error(`Error detaching scraper ${integrationId}:`, err);
    return _throw(err);
  }
};

/**
 * Queues an on-demand scrape run ("Scrape now"). Returns the run, which the
 * caller then polls for live logs.
 */
export const api_trigger_scraper_run = async (integrationId, token) => {
  try {
    const res = await axios.post(`${API}/cms/scrapers/${integrationId}/run`, {}, {
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    });
    return res;
  } catch (err) {
    console.error(`Error triggering scrape run for integration ${integrationId}:`, err);
    return _throw(err);
  }
};

/**
 * Fetches a single run (status + streamed log lines). Polled while running.
 */
export const api_get_scraper_run = async (runId, token) => {
  try {
    const res = await axios.get(`${API}/cms/scrapers/runs/${runId}`, {
      headers: { 'Authorization': token }
    });
    return res;
  } catch (err) {
    console.error(`Error fetching scrape run ${runId}:`, err);
    return _throw(err);
  }
};
